import React, { useEffect, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import config from '../../../config.json';
import walkthroughIds from './walkthroughIds';
import {
    FormControl,
    FormLabel,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    background,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    Button,
    Input,
} from 'components/v4';
import { defaultChakraSelectStyle, chakraSelectDarkMode } from '@/constants';

/*
 * Component that modals the approval content for Invoice.jsx and InvoiceLines.jsx
 * Now accepts modalData, modalState, isOpen, onClose, mode as props (no Redux)
 */

const ModalApprovalContent = ({
    modalData,
    modalState,
    isOpen,
    onClose,
    mode,
    darkMode: propDarkMode,
    onChange,
}) => {
    // darkMode can be passed as prop or fallback to false
    const darkMode = propDarkMode ?? false;
    const chakraSelectDark = chakraSelectDarkMode(darkMode);
    const inputBg = darkMode ? '#23272F' : 'white';
    const inputColor = darkMode ? 'white' : 'black';
    const borderColor = darkMode ? '#444950' : 'gray.200';
    const labelColor = darkMode ? 'white' : 'black';
    const { invoiceLines } = walkthroughIds;

    // Prefer modalData, fallback to modalState
    const invoiceStatus = modalData?.invoiceStatus ?? modalState?.invoiceStatus;
    const upload = modalData?.upload ?? modalState?.upload;

    const [options, setOptions] = useState(
        config?.invoices?.statusUpload[invoiceStatus] || [],
    );

    // Auto-select the first option if not set
    useEffect(() => {
        const opts = config?.invoices?.statusUpload[invoiceStatus] || [];
        setOptions(opts);
        if (
            (!upload || !opts.find((a) => a.upload === upload)) &&
            opts.length > 0
        ) {
            // Only auto-select if not already set
            if (onChange) {
                onChange({
                    ...(modalData || modalState),
                    upload: opts[0].upload,
                });
            }
        }
    }, [invoiceStatus]);

    // Local handler for status change
    const handleStatusChange = (v) => {
        if (onChange) {
            onChange({ ...(modalData || modalState), upload: v.upload });
        }
    };

    // Handler for Update button
    const handleUpdate = () => {
        if (onChange) {
            onChange({ ...(modalData || modalState), submit: true });
        }
        if (onClose) onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
            <ModalOverlay />
            <ModalContent
                bg={darkMode ? '#2e2e30' : 'white'}
                color={darkMode ? 'white' : 'black'}>
                <ModalHeader color={darkMode ? 'white' : 'black'}>
                    Update Invoice Status
                </ModalHeader>
                <ModalCloseButton
                    sx={{
                        color: labelColor,
                        bg: darkMode ? '#2e2e30' : 'white',
                        border: '1px solid',
                        borderColor: borderColor,
                        borderRadius: '50%',
                        boxShadow: 'md',
                        zIndex: 10,
                        top: 2,
                        right: 2,
                        position: 'absolute',
                        ':hover': {
                            bg: darkMode ? '#353A45' : '#f0f0f0',
                        },
                    }}
                    data-testid="modal-close-x"
                />
                <ModalBody>
                    <Row style={{ minHeight: 60 }}>
                        <Col>
                            <>
                                <FormControl mb={2}>
                                    <FormLabel color={labelColor}>
                                        From
                                    </FormLabel>
                                    <Input
                                        className="w-full"
                                        name="from"
                                        value={
                                            config.invoices.statusJSON[
                                                invoiceStatus
                                            ]
                                        }
                                        isDisabled
                                        data-walkthroughid={
                                            invoiceLines.ufModalFromInput
                                        }
                                        bg={inputBg}
                                        color={inputColor}
                                        borderColor={borderColor}
                                    />
                                </FormControl>
                                <FormControl
                                    dataWalkthroughid={
                                        invoiceLines.ufModalToInput
                                    }>
                                    <FormLabel color={labelColor}>To</FormLabel>
                                    <Select
                                        options={options}
                                        getOptionLabel={(opt) => opt.name}
                                        getOptionValue={(opt) => opt.upload}
                                        placeholder="Please select invoice status"
                                        value={options?.find(
                                            (a) => a.upload == upload,
                                        )}
                                        onChange={handleStatusChange}
                                        dataWalkthroughid={
                                            invoiceLines.ufModalToInput
                                        }
                                        chakraStyles={{
                                            ...defaultChakraSelectStyle,
                                            ...chakraSelectDark,
                                            control: (provided, state) => ({
                                                ...provided,
                                                background: darkMode
                                                    ? '#2e2e30'
                                                    : 'white',
                                                color: darkMode
                                                    ? 'white'
                                                    : 'black',
                                                borderColor: darkMode
                                                    ? '#444950'
                                                    : 'gray.200',
                                            }),
                                            menu: (provided, state) => ({
                                                ...provided,
                                                background: darkMode
                                                    ? '#2e2e30'
                                                    : 'white',
                                                color: darkMode
                                                    ? 'white'
                                                    : 'black',
                                            }),
                                            option: (provided, state) => ({
                                                ...provided,
                                                background: state.isSelected
                                                    ? darkMode
                                                        ? '#2e2e30'
                                                        : '#e6f7ff'
                                                    : state.isFocused
                                                      ? darkMode
                                                          ? '#2d323c'
                                                          : '#f0f0f0'
                                                      : darkMode
                                                        ? '#23272F'
                                                        : 'white',
                                                color: darkMode
                                                    ? 'white'
                                                    : 'black',
                                            }),
                                            singleValue: (provided) => ({
                                                ...provided,
                                                color: darkMode
                                                    ? 'white'
                                                    : 'black',
                                            }),
                                            input: (provided) => ({
                                                ...provided,
                                                color: darkMode
                                                    ? 'white'
                                                    : 'black',
                                            }),
                                            dropdownIndicator: (provided) => ({
                                                ...provided,
                                                color: darkMode
                                                    ? 'white'
                                                    : 'black',
                                            }),
                                            indicatorSeparator: (provided) => ({
                                                ...provided,
                                                background: darkMode
                                                    ? '#444950'
                                                    : 'gray.200',
                                            }),
                                        }}
                                    />
                                </FormControl>
                            </>
                        </Col>
                    </Row>
                    {invoiceStatus === 0 && (
                        <Alert status="warning" mt={3}>
                            <AlertIcon />
                            <AlertDescription
                                color={darkMode ? 'white' : 'black'}
                                borderRadius={4}>
                                This action can&apos;t be reverted
                            </AlertDescription>
                        </Alert>
                    )}
                    <Row style={{ marginTop: 24, marginBottom: 8 }}>
                        <Col style={{ textAlign: 'right' }}>
                            <Button
                                type="button"
                                className="chakra-button css-1n8i4of"
                                onClick={handleUpdate}
                                data-walkthroughid={
                                    invoiceLines.ufModalUpdateBtn
                                }>
                                Update
                            </Button>
                        </Col>
                    </Row>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default ModalApprovalContent;
